// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import Download from "@store/Library/Components/Toolkit/Icons/Download";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import parse from "html-react-parser";
import { fetch } from "@insite/client-framework/ServerSideRendering";
import React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    instructions = "instructions",
    templateFilePath = "templateFilePath",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.instructions]: string;
        [fields.templateFilePath]: string;
    };
}

export interface OrderDownloadTemplateStyles {
    container?: GridContainerProps;
    instructionsGridItem?: GridItemProps;
    downloadGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    instructionsText?: TypographyPresentationProps;
    downloadButton?: ButtonPresentationProps;
}

export const orderDownloadTemplateStyles: OrderDownloadTemplateStyles = {
    container: {
        css: css`
            ${spacingStyles(null, pxToRem(32))};
            gap: ${pxToRem(32)};
            padding-bottom: ${pxToRem(32)};
            border-bottom: ${pxToRem(1)} solid ${getColor("common.border")};

            &:last-child {
                padding-bottom: 0;
                border-bottom: 0;
            }
        `,
    },
    instructionsGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    downloadGridItem: {
        width: 12,
    },
    titleText: {
        variant: "h4",
        forwardAs: "h3",
    },
    downloadButton: {
        variant: "tertiary",
        icon: {
            position: "left",
            src: Download,
        },
    },
};

const styles = orderDownloadTemplateStyles;

type Props = OwnProps;

const OrderDownloadTemplate = ({ fields: { title, instructions, templateFilePath } }: Props) => {
    const handleTemplateDownload = () => {
        const fileNameArray = templateFilePath.split("/");
        const fileName = fileNameArray[fileNameArray.length - 1];

        // Used fetch to pass headers Content-Disposition to force Edge to download file instead of opening it in the browser
        fetch(templateFilePath, {
            headers: {
                "Content-Disposition": "attachement",
            },
        })
            .then(response => response.blob())
            .then(data => {
                const url = window.URL.createObjectURL(data);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            });
    };

    return (
        <GridContainer {...styles.container} data-test-selector="orderUpload_instructions">
            <GridItem {...styles.instructionsGridItem}>
                <Typography {...styles.titleText}>{title}</Typography>
                <Typography {...styles.instructionsText}>{parse(instructions, parserOptions)}</Typography>
            </GridItem>
            <GridItem {...styles.downloadGridItem}>
                <Button {...styles.downloadButton} onClick={handleTemplateDownload}>
                    {translate("Download Template")}
                </Button>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: OrderDownloadTemplate,
    definition: {
        displayName: "Greif Download Template",
        group: "Order Upload",
        allowedContexts: ["OrderUploadPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "Order Template",
                fieldType: "Translatable",
            },
            {
                name: fields.instructions,
                editorTemplate: "RichTextField",
                defaultValue: `
                    <p>With order uploads, you place an order for a predetermined quantity of products delivered over a specified period.</p>
                    <p><strong>To request order, please download the pre-determined excel template below:</strong></p>
                `,
                fieldType: "Translatable",
            },
            {
                name: fields.templateFilePath,
                displayName: "Template File",
                editorTemplate: "FilePickerField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
